import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { extractSatsFromZap, getZapSender, getZapRecipient } from './useBatchZaps';
import { AI_LABEL, WEB_KIND, isTopLevelPost, isClawstrIdentifier } from '@/lib/clawstr';

export interface RecentZap {
  zapReceipt: NostrEvent;
  targetEventId: string | null;
  senderPubkey: string | null;
  recipientPubkey: string | null;
  amount: number;
  timestamp: number;
}

interface UseRecentZapsOptions {
  /** Maximum number of zaps to return */
  limit?: number;
  /** Show all content (AI + human) instead of AI-only */
  showAll?: boolean;
}

/**
 * Fetch recent zap activity for Clawstr posts only.
 * 
 * Fetches posts and zaps in a single query function to avoid stale closure issues.
 */
export function useRecentZaps(options: UseRecentZapsOptions = {}) {
  const { nostr } = useNostr();
  const { limit = 10, showAll = false } = options;

  return useQuery({
    queryKey: ['clawstr', 'recent-zaps', limit, showAll],
    queryFn: async ({ signal }) => {
      // Step 1: Fetch recent posts
      const postFilter: NostrFilter = {
        kinds: [1111],
        '#K': [WEB_KIND],
        limit: 50,
      };

      if (!showAll) {
        postFilter['#l'] = [AI_LABEL.value];
        postFilter['#L'] = [AI_LABEL.namespace];
      }

      const posts = await nostr.query([postFilter], {
        signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]),
      });

      // Filter to valid Clawstr top-level posts
      const validPosts = posts.filter((event) => {
        if (!isTopLevelPost(event)) return false;
        const identifier = event.tags.find(([name]) => name === 'I')?.[1];
        return identifier && isClawstrIdentifier(identifier);
      });

      if (validPosts.length === 0) {
        return [];
      }

      const postIds = validPosts.map((p) => p.id);

      // Step 2: Fetch zaps for these posts
      const zapReceipts = await nostr.query(
        [{ 
          kinds: [9735], 
          '#e': postIds,
          limit: limit * 3,
        }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) }
      );

      // Sort by created_at descending (most recent first)
      const sortedZaps = zapReceipts.sort((a, b) => b.created_at - a.created_at);

      // Process zaps
      const recentZaps: RecentZap[] = [];

      for (const zap of sortedZaps) {
        if (recentZaps.length >= limit) break;

        const eTag = zap.tags.find(([name]) => name === 'e');
        const targetEventId = eTag?.[1] ?? null;

        if (!targetEventId || !postIds.includes(targetEventId)) continue;

        const senderPubkey = getZapSender(zap);
        const recipientPubkey = getZapRecipient(zap);
        const amount = extractSatsFromZap(zap);

        if (amount === 0) continue;

        recentZaps.push({
          zapReceipt: zap,
          targetEventId,
          senderPubkey,
          recipientPubkey,
          amount,
          timestamp: zap.created_at,
        });
      }

      return recentZaps;
    },
    staleTime: 30 * 1000,
  });
}
